import React, { useEffect, useContext, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Table,
  Form,
  Spinner,
  Alert,
  Modal,
} from "react-bootstrap";
import { Calendar, Clock, MapPin, X, CheckCircle, AlertCircle, PauseCircle, Download } from "react-feather";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb, faBaseball } from "@fortawesome/free-solid-svg-icons";

// Componentes
import SearchInputRes from "../../components/searchInputRes/SearchInputRes";

// Contextos
import reservasContext from "../../context/reservas/reservasContext";
import authContext from "../../context/authentication/authContex";
import informesContext from "../../context/informesContext/informesContext";

// API
import { getAllUsers } from "../../api/user";
import * as XLSX from 'xlsx';

// Estilos
import "./MisReservas.css";
import normalizeText from "../../utils/normalizeText";

// Indica si una reserva tiene cargos adicionales, ya sea a nivel reserva
// (cargosAdicionales) o a nivel de algún jugador (jugadores[].servicios).
const tieneCargosAdicionales = (r) =>
  (r.cargosAdicionales?.length > 0) ||
  (r.jugadores || []).some((j) => j.servicios?.length > 0);

// Formatea la cantidad de "Luz" (guardada en segundos u horas) como "1:30 hs".
const formatLuz = (cantidad) => {
  const totalSec = cantidad >= 60 ? cantidad : cantidad * 3600;
  const h = Math.floor(totalSec / 3600);
  const m = Math.round((totalSec % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")} hs`;
};

// Formatea una cantidad genérica como "0,5 unid." (coma decimal).
const formatUnidades = (cantidad) => `${String(cantidad).replace(".", ",")} unid.`;

// Renderiza una lista de cargos/servicios ({ concepto, cantidad }) con su ícono a la izquierda:
// - "Luz": ícono de luz y luego el tiempo "1:30 hs".
// - "Tubo de pelotas": ícono de pelota y luego "0,5 unid.".
// - Otros conceptos: texto "concepto: X unid." como fallback.
const CargosList = ({ cargos }) =>
  (cargos || []).map((c, i) => {
    if (c.concepto === "Luz") {
      return (
        <div key={i} className="d-flex align-items-center gap-1">
          <FontAwesomeIcon icon={faLightbulb} style={{ color: "#f59e0b", fontSize: "0.75rem" }} />
          <small className="text-muted">{formatLuz(c.cantidad)}</small>
        </div>
      );
    }
    if (c.concepto === "Tubo de pelotas") {
      return (
        <div key={i} className="d-flex align-items-center gap-1">
          <FontAwesomeIcon icon={faBaseball} style={{ color: "#84cc16", fontSize: "0.75rem" }} />
          <small className="text-muted">{formatUnidades(c.cantidad)}</small>
        </div>
      );
    }
    return (
      <div key={i}>
        <small className="text-muted">{`${c.concepto}: ${formatUnidades(c.cantidad)}`}</small>
      </div>
    );
  });

function MisReservas({ hideTitle = false }) {
  const rContext = useContext(reservasContext);
  const { misReservas, loading, obtenerMisReservas } = rContext;

  const aContext = useContext(authContext);
  const { usuario } = aContext;

  const iContext = useContext(informesContext);
  const { generarInformeExcel } = iContext;

  const [filtroEstado, setFiltroEstado] = useState("futuras");
  const [incluirComoJugador, setIncluirComoJugador] = useState(false);
  const [reservasFiltradas, setReservasFiltradas] = useState([]);
  const [busquedaJugador, setBusquedaJugador] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  // Estado del modal de cobros por concepto
  const [showModalCobros, setShowModalCobros] = useState(false);
  const [cobrosConcepto, setCobrosConcepto] = useState("DOBLES");
  const [cobrosPrecioSlot, setCobrosPrecioSlot] = useState("");

  const SINGLES_TIPOS = ["SINGLES", "SINGLES_45", "SINGLES_PADEL"];
  const DOBLES_TIPOS = ["DOBLES", "DOBLES_60", "DOBLES_90", "DOBLES_PADEL"];
  const esPrecioPorTubo = cobrosConcepto === "TUBO DE PELOTAS";

  // Verificar si el usuario es Admin, Gestor de Reservas o Revisor de Reservas
  // El Revisor de Reservas tiene la misma vista que el Admin: ve TODAS las reservas.
  const esAdminOGestor = usuario?.userType?.some(ut =>
    ['ADMIN', 'ADMINNOABM', 'GESTOR_RESERVAS', 'REVISOR_RESERVAS'].includes(ut.typeName)
  );

  // Cargar reservas al montar - traemos TODAS para filtrar en cliente
  // El backend detecta automáticamente si es Admin/Gestor y trae todas las reservas
  useEffect(() => {
    if (usuario) {
      obtenerMisReservas(false, null, incluirComoJugador); // soloFuturas=false, estado=null para traer todo
    }
  }, [usuario, incluirComoJugador]);

  // Filtrar reservas según el filtro seleccionado y búsqueda de jugador
  useEffect(() => {
    if (!misReservas) return;

    const ahora = new Date();

    let filtradas = misReservas;

    // Filtrar por estado
    switch (filtroEstado) {
      case "futuras":
        filtradas = misReservas.filter((r) => {
          // Comparar fecha + horaFin para determinar si es futura
          const fechaReserva = new Date(r.fecha);
          // Extraer componentes en UTC para evitar conversión de zona horaria
          const year = fechaReserva.getUTCFullYear();
          const month = fechaReserva.getUTCMonth();
          const day = fechaReserva.getUTCDate();
          const fechaLocal = new Date(year, month, day);
          const [horas, minutos] = r.horaFin.split(":");
          fechaLocal.setHours(parseInt(horas), parseInt(minutos), 0);
          return fechaLocal > ahora && r.estado === "CONFIRMADA";
        });
        break;
      case "pasadas":
        filtradas = misReservas.filter((r) => {
          // Comparar fecha + horaFin para determinar si ya pasó
          const fechaReserva = new Date(r.fecha);
          // Extraer componentes en UTC para evitar conversión de zona horaria
          const year = fechaReserva.getUTCFullYear();
          const month = fechaReserva.getUTCMonth();
          const day = fechaReserva.getUTCDate();
          const fechaLocal = new Date(year, month, day);
          const [horas, minutos] = r.horaFin.split(":");
          fechaLocal.setHours(parseInt(horas), parseInt(minutos), 0);
          return fechaLocal <= ahora && (r.estado === "CONFIRMADA" || r.estado === "COMPLETADA");
        });
        break;
      case "canceladas":
        filtradas = misReservas.filter((r) => r.estado === "CANCELADA");
        break;
      case "suspendidas":
        filtradas = misReservas.filter((r) => r.estado === "SUSPENDIDA");
        break;
      case "con_cargos":
        filtradas = misReservas.filter(tieneCargosAdicionales);
        break;
      case "todas":
        filtradas = misReservas;
        break;
      default:
        filtradas = misReservas;
    }

    // Filtro de rango de fechas (solo admin/gestor, aplica a cualquier estado)
    if (esAdminOGestor && (fechaDesde || fechaHasta)) {
      filtradas = filtradas.filter((r) => {
        const fechaReserva = new Date(r.fecha);
        const year = fechaReserva.getUTCFullYear();
        const month = fechaReserva.getUTCMonth();
        const day = fechaReserva.getUTCDate();
        const fechaSolo = new Date(year, month, day);
        if (fechaDesde) {
          const desde = new Date(fechaDesde + "T00:00:00");
          if (fechaSolo < desde) return false;
        }
        if (fechaHasta) {
          const hasta = new Date(fechaHasta + "T23:59:59");
          if (fechaSolo > hasta) return false;
        }
        return true;
      });
    }

    // Filtrar por búsqueda de jugador (solo si hay búsqueda)
    if (busquedaJugador && busquedaJugador.trim() !== "") {
      const textoBusqueda = busquedaJugador.trim();

      // Detectar si está entre comillas dobles para búsqueda exacta
      const esExacta = textoBusqueda.startsWith('"') && textoBusqueda.endsWith('"');
      const terminoBusqueda = esExacta
        ? normalizeText(textoBusqueda.slice(1, -1))
        : normalizeText(textoBusqueda);

      filtradas = filtradas.filter((r) => {
        // Buscar en el nombre del usuario que reservó
        const nombreReservador = normalizeText(r.userFullName || r.userName || "");

        // Buscar en los nombres de los jugadores
        const nombresJugadores = (r.jugadores || [])
          .map(j => normalizeText(j.nombre || ""))
          .join(" ");

        const textoCompleto = `${nombreReservador} ${nombresJugadores}`;

        if (esExacta) {
          // Búsqueda exacta
          return textoCompleto.includes(terminoBusqueda);
        } else {
          // Búsqueda flexible
          return textoCompleto.includes(terminoBusqueda);
        }
      });
    }

    // Ordenar por fecha descendente
    filtradas.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

    setReservasFiltradas(filtradas);
  }, [misReservas, filtroEstado, busquedaJugador, fechaDesde, fechaHasta]);

  // Formatear fecha (maneja correctamente zona horaria local)
  const formatearFecha = (fecha) => {
    const f = new Date(fecha);
    // Extraer componentes en UTC para evitar conversión de zona horaria
    const year = f.getUTCFullYear();
    const month = f.getUTCMonth();
    const day = f.getUTCDate();
    // Crear fecha local con esos componentes
    const fechaLocal = new Date(year, month, day);
    const opciones = { weekday: "short", day: "numeric", month: "short", year: "numeric" };
    return fechaLocal.toLocaleDateString("es-AR", opciones);
  };

  // Formatear fecha completa (maneja correctamente zona horaria local)
  const formatearFechaCompleta = (fecha) => {
    const f = new Date(fecha);
    // Extraer componentes en UTC para evitar conversión de zona horaria
    const year = f.getUTCFullYear();
    const month = f.getUTCMonth();
    const day = f.getUTCDate();
    // Crear fecha local con esos componentes
    const fechaLocal = new Date(year, month, day);
    const opciones = { weekday: "long", day: "numeric", month: "long", year: "numeric" };
    return fechaLocal.toLocaleDateString("es-AR", opciones);
  };

  // Obtener badge de estado
  const getBadgeEstado = (estado) => {
    switch (estado) {
      case "CONFIRMADA":
        return (
          <Badge bg="success">
            <CheckCircle size={14} className="me-1" />
            Confirmada
          </Badge>
        );
      case "CANCELADA":
        return (
          <Badge bg="danger">
            <X size={14} className="me-1" />
            Cancelada
          </Badge>
        );
      case "COMPLETADA":
        return (
          <Badge bg="secondary">
            <CheckCircle size={14} className="me-1" />
            Completada
          </Badge>
        );
      case "NO_SHOW":
        return (
          <Badge bg="warning">
            <AlertCircle size={14} className="me-1" />
            No asistió
          </Badge>
        );
      case "SUSPENDIDA":
        return (
          <Badge bg="secondary">
            <PauseCircle size={14} className="me-1" />
            Suspendida
          </Badge>
        );
      default:
        return <Badge bg="secondary">{estado}</Badge>;
    }
  };

  // Exportar a Excel - Cobros por concepto: una fila por jugador-por-reserva
  const exportarCobros = async () => {
    const precio = Number(cobrosPrecioSlot);
    if (!precio || precio <= 0) {
      alert(esPrecioPorTubo ? "Ingresá el precio del tubo mayor a 0" : "Ingresá un precio por slot de 15 min mayor a 0");
      return;
    }

    // Traer usuarios para resolver nroSocio y fullName (populate manual a la coleccion users)
    let mapaNroSocio = {};
    let mapaFullName = {};
    try {
      const resp = await getAllUsers();
      const usuarios = resp.data.data || resp.data;
      usuarios.forEach((u) => {
        if (u.userName) {
          mapaNroSocio[u.userName] = u.nroSocio || "";
          mapaFullName[u.userName] = u.fullName || "";
        }
      });
    } catch (e) {
      console.error("Error al obtener usuarios para nroSocio:", e);
    }

    const matcheaConcepto = (reserva) => {
      if (cobrosConcepto === "SINGLES") return SINGLES_TIPOS.includes(reserva.tipoJuego);
      if (cobrosConcepto === "DOBLES") return DOBLES_TIPOS.includes(reserva.tipoJuego);
      return true;
    };

    // Armar filas agrupadas por socio + concepto. Para SINGLES/DOBLES el concepto de la
    // fila es el tipoJuego real de la reserva (ej: SINGLES_45, DOBLES_PADEL), de modo que
    // un socio con 2 SINGLES + 1 SINGLES_45 aparece en 2 filas. Para LUZ y TUBO DE PELOTAS
    // el concepto es fijo, por lo que cada socio queda en 1 sola fila.
    // Importe por jugador:
    //  - SINGLES/DOBLES: unidades15 (a partir de duracionMinutos) × precio.
    //  - LUZ: cantidad imputada en jugador.servicios (concepto "Luz") convertida a unidades
    //    de 15 min × precio. Cantidad >= 60 se interpreta en segundos; si no, en horas.
    //  - TUBO DE PELOTAS: cantidad imputada en jugador.servicios (concepto "Tubo de pelotas") × precio.
    const agrupaPorTipoJuego = cobrosConcepto === "SINGLES" || cobrosConcepto === "DOBLES";
    const esLuz = cobrosConcepto === "LUZ";
    const ES_INVITADO_PATTERN = /^Invitado\d+$/i;
    const mapaFilas = new Map();

    reservasFiltradas.forEach((reserva) => {
      if (!matcheaConcepto(reserva)) return;
      if (!reserva.jugadores || reserva.jugadores.length === 0) return;

      const unidades15Reserva = Math.round((reserva.duracionMinutos || 0) / 15);
      const conceptoFila = agrupaPorTipoJuego ? reserva.tipoJuego : cobrosConcepto;
      const reservaTieneInvitado = reserva.jugadores.some(j => ES_INVITADO_PATTERN.test(j.userNameJ || ""));

      reserva.jugadores.forEach((jugador) => {
        // Excluir jugadores sin userNameJ (invitados sin cuenta, no se pueden cobrar)
        if (!jugador.userNameJ) return;

        let importePorJugador = 0;
        if (agrupaPorTipoJuego) {
          importePorJugador = Math.round(unidades15Reserva * precio * 100) / 100;
        } else if (esLuz) {
          const luzRaw = (jugador.servicios || []).find(s => s.concepto === "Luz")?.cantidad;
          if (luzRaw == null || luzRaw <= 0) return;
          // >= 60 se asume en segundos; si no, en horas
          const totalSeg = luzRaw >= 60 ? luzRaw : luzRaw * 3600;
          const unidades15Luz = totalSeg / 900;
          importePorJugador = Math.round(unidades15Luz * precio * 100) / 100;
          // Si la reserva tiene un Invitado####, el importe de luz se duplica para cada jugador
          if (reservaTieneInvitado) {
            importePorJugador = Math.round(importePorJugador * 2 * 100) / 100;
          }
        } else {
          // TUBO DE PELOTAS
          const tubos = (jugador.servicios || []).find(s => s.concepto === "Tubo de pelotas")?.cantidad;
          if (tubos == null || tubos <= 0) return;
          importePorJugador = Math.round(Number(tubos) * precio * 100) / 100;
        }

        if (importePorJugador <= 0) return;

        // En SINGLES/DOBLES, los cobros de un Invitado#### se atribuyen al primer
        // jugador de la reserva, en una fila aparte de la suya propia.
        let userNameKey = jugador.userNameJ;
        let nroSocio = mapaNroSocio[jugador.userNameJ] || "";
        let nombreCompleto = mapaFullName[jugador.userNameJ] || jugador.nombre || "";
        let keySuffix = "";

        if (agrupaPorTipoJuego && ES_INVITADO_PATTERN.test(jugador.userNameJ)) {
          const primerJugador = reserva.jugadores[0];
          if (
            primerJugador &&
            primerJugador.userNameJ &&
            !ES_INVITADO_PATTERN.test(primerJugador.userNameJ)
          ) {
            userNameKey = primerJugador.userNameJ;
            nroSocio = mapaNroSocio[primerJugador.userNameJ] || "";
            nombreCompleto = mapaFullName[primerJugador.userNameJ] || primerJugador.nombre || "";
            keySuffix = "|inv";
          }
        } else if (esLuz && reservaTieneInvitado) {
          // Filas de luz provenientes de reservas con invitado se marcan en motivo
          // y se separan de las filas normales del mismo jugador.
          keySuffix = "|inv";
        }

        const key = `${userNameKey}|${conceptoFila}${keySuffix}`;
        const motivoFila = keySuffix === "|inv" ? `${conceptoFila} INVITADO` : conceptoFila;
        const existente = mapaFilas.get(key);
        if (existente) {
          existente.importe = Math.round((existente.importe + importePorJugador) * 100) / 100;
        } else {
          mapaFilas.set(key, {
            nroSocio,
            nombreCompleto,
            concepto: "",
            importe: importePorJugador,
            motivo: motivoFila,
          });
        }
      });
    });

    const filas = Array.from(mapaFilas.values());

    // Ordenar por nroSocio (los vacios al final) y luego por concepto
    filas.sort((a, b) => {
      if (!a.nroSocio && b.nroSocio) return 1;
      if (a.nroSocio && !b.nroSocio) return -1;
      if (a.nroSocio !== b.nroSocio) {
        return a.nroSocio.toString().localeCompare(b.nroSocio.toString(), 'es', { numeric: true });
      }
      return a.motivo.localeCompare(b.motivo, 'es');
    });

    // Generar xlsx con 5 columnas: Numero de Socio | Nombre Completo | Concepto | Importe | Motivo
    const aoa = [
      ["Número de Socio", "Nombre Completo", "Concepto", "Importe", "Motivo"],
      ...filas.map(f => [f.nroSocio, f.nombreCompleto, f.concepto, f.importe, f.motivo]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 16 }, { wch: 28 }, { wch: 18 }, { wch: 12 }, { wch: 30 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cobros');
    const fechaParaNombre = (f) => f ? f.split('-').reverse().join('') : 'DDMMYYYY';
    const sufijoFechas = `_${fechaParaNombre(fechaDesde)}-${fechaParaNombre(fechaHasta)}`;
    XLSX.writeFile(wb, `Cobros_${cobrosConcepto.replace(/\s+/g, '_')}${sufijoFechas}.xlsx`);

    setShowModalCobros(false);
  };

  return (
    <Container fluid className="mis-reservas-container-compact">
      {!hideTitle && (
        <Row>
          <Col>
            <h2 className="page-title">
              <Calendar size={32} className="me-2" />
              Mis Reservas
            </h2>
          </Col>
        </Row>
      )}

      {/* Filtros */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="align-items-end">
            <Col md={esAdminOGestor ? 3 : 4}>
              <Form.Group>
                <Form.Label>
                  <strong>Filtrar por:</strong>
                </Form.Label>
                <Form.Select
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value)}
                >
                  <option value="futuras">Reservas Futuras</option>
                  <option value="pasadas">Reservas Pasadas</option>
                  <option value="canceladas">Canceladas</option>
                  <option value="suspendidas">Suspendidas</option>
                  <option value="con_cargos">Con cargos adicionales</option>
                  <option value="todas">Todas</option>
                </Form.Select>
              </Form.Group>
            </Col>
            {esAdminOGestor && (
              <>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label><strong>Desde:</strong></Form.Label>
                    <Form.Control
                      type="date"
                      value={fechaDesde}
                      onChange={(e) => setFechaDesde(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label><strong>Hasta:</strong></Form.Label>
                    <Form.Control
                      type="date"
                      value={fechaHasta}
                      onChange={(e) => setFechaHasta(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </>
            )}
            {!esAdminOGestor && (
              <Col md={4}>
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    id="incluirComoJugador"
                    label='Incluir "No reservé yo, pero soy jugador"'
                    checked={incluirComoJugador}
                    onChange={(e) => setIncluirComoJugador(e.target.checked)}
                  />
                </Form.Group>
              </Col>
            )}
            <Col md={esAdminOGestor ? 3 : 4} className="text-end">
              <div style={{ paddingTop: esAdminOGestor ? "1.5rem" : 0 }}>
                <p className="mb-1 text-muted">
                  Total de reservas: <strong>{reservasFiltradas?.length || 0}</strong>
                </p>
                {esAdminOGestor && reservasFiltradas.length > 0 && (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => setShowModalCobros(true)}
                    style={{
                      padding: "0.25rem 0.5rem",
                      backgroundColor: "#34A853",
                      borderColor: "#34A853",
                      color: "white",
                      boxShadow: "0 0 2.5px black",
                      borderRadius: "10px",
                    }}
                  >
                    <Download size={14} className="me-1" />
                    Descargar .xlsx
                  </Button>
                )}
              </div>
            </Col>
          </Row>

          {/* Buscador de jugadores (solo Admin/Gestor) - Segunda fila */}
          {esAdminOGestor && (
            <Row className="mt-3">
              <Col md={12}>
                <SearchInputRes
                  filter={busquedaJugador}
                  buscar={setBusquedaJugador}
                />
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Lista de reservas */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Cargando reservas...</p>
            </div>
          ) : !reservasFiltradas || reservasFiltradas.length === 0 ? (
            <Alert variant="info" className="text-center">
              <Calendar size={48} className="mb-3" />
              <p className="mb-0">No tienes reservas {filtroEstado === "con_cargos" ? "con cargos adicionales" : filtroEstado}</p>
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table hover className="mis-reservas-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Horario</th>
                    <th>Cancha</th>
                    <th>Tipo</th>
                    <th>Jugadores</th>
                    <th>Estado</th>
                    <th>Reservado por</th>
                  </tr>
                </thead>
                <tbody>
                  {reservasFiltradas.map((reserva) => (
                    <tr key={reserva._id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <Calendar size={16} className="me-2 text-muted" />
                          <div>
                            <div>{formatearFecha(reserva.fecha)}</div>
                            <small className="text-muted">
                              {formatearFechaCompleta(reserva.fecha)}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <Clock size={16} className="me-2 text-muted" />
                        {reserva.horaInicio} - {reserva.horaFin}
                      </td>
                      <td>
                        <MapPin size={16} className="me-2 text-muted" />
                        {reserva.nameActivo}
                      </td>
                      <td>
                        <Badge bg={reserva.tipoJuego === "SINGLES" || reserva.tipoJuego === "SINGLES_45" ? "primary" : reserva.tipoJuego === "DOBLES_90" ? "success" : "info"}>
                          {reserva.tipoJuego === "SINGLES" ? "Singles (60 min)" :
                           reserva.tipoJuego === "SINGLES_45" ? "Singles (45 min)" :
                           reserva.tipoJuego === "DOBLES" ? "Dobles (75 min)" :
                           reserva.tipoJuego === "DOBLES_60" ? "Dobles (60 min)" :
                           reserva.tipoJuego === "DOBLES_90" ? "Dobles (90 min)" :
                           reserva.tipoJuego}
                        </Badge>
                      </td>
                      <td>
                        {reserva.jugadores?.length > 0 ? (
                          <div>
                            {reserva.jugadores.map((jugador, idx) => (
                              <div key={idx}>
                                <small>{jugador.nombre}</small>
                                {jugador.servicios?.length > 0 && (
                                  <div className="ms-3">
                                    <CargosList cargos={jugador.servicios} />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <small className="text-muted">Sin jugadores</small>
                        )}
                      </td>
                      <td>{getBadgeEstado(reserva.estado)}</td>
                      <td>
                        {reserva.userFullName || reserva.userName || "Usuario"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal de Cobros por concepto */}
      <Modal show={showModalCobros} onHide={() => setShowModalCobros(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cobros por concepto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Genera un Excel con una fila por cada jugador de cada reserva según el concepto elegido, tomando como base las reservas actualmente filtradas en la vista.
          </p>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label><strong>Concepto</strong></Form.Label>
              <Form.Select
                value={cobrosConcepto}
                onChange={(e) => setCobrosConcepto(e.target.value)}
              >
                <option value="SINGLES">SINGLES</option>
                <option value="DOBLES">DOBLES</option>
                <option value="LUZ">LUZ</option>
                <option value="TUBO DE PELOTAS">TUBO DE PELOTAS</option>
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>
                <strong>{esPrecioPorTubo ? "Precio del tubo" : "Precio por slot de 15 min"}</strong>
              </Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={cobrosPrecioSlot}
                onChange={(e) => setCobrosPrecioSlot(e.target.value)}
                placeholder="Ej: 500"
              />
              <Form.Text className="text-muted">
                {esPrecioPorTubo
                  ? "El importe se calcula como (cantidad de tubos imputados al socio) × este precio."
                  : cobrosConcepto === "LUZ"
                    ? "El importe se calcula como (luz imputada al socio, en unidades de 15 min) × este precio."
                    : "El importe se calcula como (duración de la reserva en minutos / 15) × este precio."}
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalCobros(false)}>
            Cancelar
          </Button>
          <Button variant="success" onClick={exportarCobros}>
            <Download size={14} className="me-1" />
            Descargar
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}

export default MisReservas;
